import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import clone from 'ramda/src/clone';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import Avatar from '@components/@core/Avatar';
import BlogAdvancedSearch from '@components/BlogAdvancedSearch';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import Thumbnail from '@components/@core/Thumbnail';
import { getImageURL } from '~/services/util';

const Header = () => (
    <Row className="mx-0 border-bottom desktop-flex">
        <Col xs={1} className="px-0">
            <p className="dsl-m16 my-2">Image</p>
        </Col>
        <Col xs={3}>
            <p className="dsl-m16 my-2">Title</p>
        </Col>
        <Col xs={2} className="pl-0">
            <p className="dsl-m16 my-2">Created</p>
        </Col>
        <Col xs={3} className="pr-0">
            <p className="dsl-m16 my-2">Categories</p>
        </Col>
        <Col xs={3} className="pr-0">
            <p className="dsl-m16 my-2">Author</p>
        </Col>
    </Row>
);

const Blog = ({ data, author, editable, onSelect }) => {
    const categoryNames = data?.data?.blog_categories ? data?.data?.blog_categories.join(', ') : 'Na';
    const url = getImageURL(data.data?.body);

    return (
        <Row className="d-flex ssm-column mx-0 py-3 border-bottom" onClick={() => onSelect('edit')}>
            <Col xs={12} sm={1} className="px-0">
                <Thumbnail className="responsive-thumbnail" size="responsive" source={url} />
            </Col>
            <Col xs={12} sm={3} className="d-h-between pe-0 ps-3 ssp-l-0 mobile-screen my-2 my-sm-0">
                <div className="d-h-start">
                    <Avatar type="logo" url={author?.avatar_cid} isSponsored={true} />
                    <p className="dsl-b16 ms-2 mb-0">{`${author?.first_name} ${author?.last_name}`}</p>
                </div>
                {editable && <EditDropdown options={['Add new post', 'Delete', 'Edit']} onChange={onSelect} />}
            </Col>
            <Col xs={12} sm={3} className="d-flex px-0 px-sm-3 my-2 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Title</label>
                <p className="dsl-b16 truncate-two text-400 mb-0">{data?.name}</p>
            </Col>
            <Col xs={12} sm={2} className="d-flex px-0 justify-content-start my-2 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Created At</label>
                <p className="dsl-b16 text-end mb-0">{moment(data?.created_at).format('MMM DD, YY')}</p>
            </Col>
            <Col xs={12} sm={3} className="d-flex pe-0 ps-3 ssp-l-0 my-2 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Category</label>
                <p className="dsl-b16 truncate-two mb-0">{categoryNames}</p>
            </Col>
            <Col xs={12} sm={3} className="d-h-between pe-0 ps-3 ssp-l-0 desktop-flex">
                <div className="d-h-start">
                    <Avatar type="logo" url={data?.author_avatar} isSponsored={true} />
                    <p className="dsl-b16 ms-2 mb-0">{`${author?.first_name} ${author?.last_name}`}</p>
                </div>
                {editable && <EditDropdown options={['Add new post', 'Delete', 'Edit']} onChange={onSelect} />}
            </Col>
        </Row>
    );
};

const Drafts = ({ data, authors, categories, departments, search, onSelect, onSearch }) => {
    const total = data?.total;
    const user = useSelector((state) => state.app.user?.community_user);

    const [searchTerms, setSearchTerms] = useState({
        search: null,
        author: null,
        department: null,
        category: null,
    });

    useEffect(() => {
        if (!searchTerms.search) return;
        const emptyTerms = searchTerms.search === '' && !searchTerms.author && !searchTerms.category && !searchTerms.department;
        if (emptyTerms) {
            onSearch(null);
        } else {
            onSearch({
                search: searchTerms.search,
                author: searchTerms.author ? [searchTerms.author.id] : null,
                blogCategory: searchTerms.category ? [searchTerms.category] : null,
                categoryId: searchTerms.department ? [searchTerms.department.id] : null,
            });
        }
    }, [searchTerms]);

    const handleSelectMenu = (item) => (e) => {
        if (e === 'add new post') {
            onSelect(e);
        } else {
            onSelect(e, item);
        }
    };

    const handleSearch = (e) => {
        if (onSearch) onSearch(e);
        const author = e.author ? find(propEq('id', e.author[0]), authors) : null;
        const department = e.categoryId ? find(propEq('id', e.categoryId[0]), departments) : null;
        const category = e.blogCategory ? e.blogCategory[0] : null;
        setSearchTerms({ search: e.search, author, department, category });
    };

    const handleCancelSearchTerm = (type) => () => {
        const terms = clone(searchTerms);
        terms[type] = null;
        setSearchTerms(terms);
    };

    return (
        <div className="blog-admin-drafts">
            {search ? (
                <>
                    <p className="dsl-b20 bold mt-3 my-1">Search Results</p>
                    <div className="d-h-start py-2">
                        {searchTerms.author && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('author')}>
                                <span className="dsl-b14">{`${searchTerms.author.first_name} ${searchTerms.author.last_name}`}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.search && searchTerms.search !== '' && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('search')}>
                                <span className="dsl-b14">{searchTerms.search}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.category && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('category')}>
                                <span className="dsl-b14">{searchTerms.category}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.department && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('department')}>
                                <span className="dsl-b14">{searchTerms.department.name}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="d-h-end py-2">
                    <BlogAdvancedSearch authors={authors} categories={categories} departments={departments} onSearch={handleSearch} />
                    <Icon name="fas fa-chart-pie-alt" size={14} />
                </div>
            )}
            {total > 0 ? (
                <>
                    <Header />
                    {data?.data &&
                        data.data.map((item) => {
                            const authorId = item.user_id || item.data?.writer_id;
                            const author = item.author || find(propEq('id', authorId), authors);
                            const editable = !!find(propEq('id', user?.id), authors) || !!user?.super_admin;
                            return <Blog key={`draft-${item.id}`} data={item} author={author} editable={editable} onSelect={handleSelectMenu(item)} />;
                        })}
                </>
            ) : (
                <p className="dsl-b16">There are no drafts posts to show.</p>
            )}
            <div className={`${data?.last_page > 1 ? 'd-h-between' : 'd-h-end'} mt-3`}>
                <Pagination total={data?.last_page} current={data?.current_page} pers={[]} />
                <Button type="high" name="ADD POST" onClick={() => onSelect('add new post')} />
            </div>
        </div>
    );
};

Drafts.propTypes = {
    data: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        total: PropTypes.number,
    }),
    authors: PropTypes.array,
    categories: PropTypes.array,
    departments: PropTypes.array,
    editable: PropTypes.bool,
    search: PropTypes.bool,
    onSelect: PropTypes.func,
    onSearch: PropTypes.func,
};

Drafts.defaultProps = {
    data: {
        current_page: 1,
        data: [],
        last_page: 1,
        total: 0,
    },
    authors: [],
    categories: [],
    departments: [],
    editable: false,
    search: false,
    onSelect: () => {},
    onSearch: () => {},
};

export default Drafts;
